import React, { useState, useEffect } from "react";
import countryStateData from "../../data/country_state.json";

const steps = [
  "Contact Details",
  "Traveller Details",
  "Address & Payment",
];

/* ================= CURRENCY CONFIG ================= */

const NMI_CURRENCIES = ["USD", "GBP", "CAD", "EUR"];

const STRIPE_CURRENCIES = [
  "USD",
  "GBP",
  "INR",
  "CAD",
  "EUR",
  "AUD",
  "AED",
  "JPY",
  "MXN",
  "SGD",
  "CNY",
  "NZD",
];

export default function SelfPaymentForm({ onComplete }) {
  const [step, setStep] = useState(0);

  const [form, setForm] = useState({
    /* Contact */
    email: "",
    phone: "",
    bookingNumber: "",

    /* Traveller */
    title: "",
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",

    /* Address */
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "",

    /* Payment */
    amount: "",
    currency: "USD",
    gateway: "stripe",
  });

  const update = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  /* ================= VALIDATION ================= */

  const valid = () => {
    if (step === 0) {
      return (
        /^\S+@\S+\.\S+$/.test(form.email) &&
        form.phone.length >= 8 &&
        form.bookingNumber.length > 2
      );
    }

    if (step === 1) {
      return (
        form.title &&
        form.firstName.length > 1 &&
        form.lastName.length > 1 &&
        form.gender
      );
    }

    if (step === 2) {
      return (
        form.address1 &&
        form.city &&
        form.country &&
        form.zip &&
        form.amount &&
        Number(form.amount) > 0 &&
        form.currency &&
        form.gateway
      );
    }

    return false;
  };

  /* ================= HELPERS ================= */

  const selectedCountry = countryStateData.find(
    (c) => c.countryCode === form.country
  );

  const states = selectedCountry?.stateProvinces || [];

  const availableCurrencies =
    form.gateway === "nmi" ? NMI_CURRENCIES : STRIPE_CURRENCIES;

  /* ================= AUTO-FIX INVALID COMBOS ================= */

  useEffect(() => {
    if (!availableCurrencies.includes(form.currency)) {
      setForm((f) => ({
        ...f,
        currency: availableCurrencies[0],
      }));
    }
  }, [form.gateway]);

  /* ================= UI ================= */

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b text-center">
          <h1 className="text-2xl font-semibold text-emerald-600">
            BookTrip4Me – Create Payment Link
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Admin-only • Customer will only receive payment link
          </p>
        </div>

        {/* Stepper */}
        <div className="flex justify-between px-6 py-4">
          {steps.map((label, i) => (
            <div key={i} className="flex-1 text-center">
              <div
                className={`mx-auto w-8 h-8 flex items-center justify-center rounded-full text-sm font-semibold
                ${
                  i <= step
                    ? "bg-emerald-600 text-white"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                {i + 1}
              </div>
              <p
                className={`text-xs mt-2 ${
                  i === step ? "text-emerald-600" : "text-gray-400"
                }`}
              >
                {label}
              </p>
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-5">

          {/* STEP 1 */}
          {step === 0 && (
            <>
              <Input label="Email" name="email" onChange={update} />
              <Input label="Phone" name="phone" onChange={update} />
              <Input label="Booking Number" name="bookingNumber" onChange={update} />
            </>
          )}

          {/* STEP 2 */}
          {step === 1 && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Select label="Title" name="title" onChange={update}>
                  <option value="">Select</option>
                  <option>Mr</option>
                  <option>Ms</option>
                  <option>Mrs</option>
                </Select>

                <Select label="Gender" name="gender" onChange={update}>
                  <option value="">Select</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </Select>
              </div>

              <Input label="First Name" name="firstName" onChange={update} />
              <Input label="Middle Name (Optional)" name="middleName" onChange={update} />
              <Input label="Last Name" name="lastName" onChange={update} />
            </>
          )}

          {/* STEP 3 */}
          {step === 2 && (
            <>
              <Input label="Address Line 1" name="address1" onChange={update} />
              <Input label="Address Line 2" name="address2" onChange={update} />
              <Input label="City" name="city" onChange={update} />

              <Select
                label="Country"
                name="country"
                onChange={(e) =>
                  setForm({ ...form, country: e.target.value, state: "" })
                }
              >
                <option value="">Select Country</option>
                {countryStateData.map((c) => (
                  <option key={c.countryCode} value={c.countryCode}>
                    {c.name}
                  </option>
                ))}
              </Select>

              {form.country && states.length > 0 ? (
                <Select label="State" name="state" onChange={update}>
                  <option value="">Select State</option>
                  {states.map((s, i) => (
                    <option key={i}>{s.name}</option>
                  ))}
                </Select>
              ) : (
                form.country && (
                  <Input label="State / Province" name="state" onChange={update} />
                )
              )}

              <Input label="ZIP / Postal Code" name="zip" onChange={update} />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={`Amount (${form.currency})`}
                  name="amount"
                  type="number"
                  min="1"
                  onWheel={(e) => e.target.blur()}
                  onChange={update}
                />

                <Select
                  label="Currency"
                  name="currency"
                  value={form.currency}
                  onChange={update}
                >
                  {availableCurrencies.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Select>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">
                  Payment Gateway
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <GatewayCard
                    selected={form.gateway === "stripe"}
                    onClick={() => setForm({ ...form, gateway: "stripe" })}
                    img="/assets/stripe.png"
                    title="Stripe"
                  />
                  <GatewayCard
                    selected={form.gateway === "nmi"}
                    onClick={() => setForm({ ...form, gateway: "nmi" })}
                    img="/assets/nmi.webp"
                    title="NMI"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-between">
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} className="text-sm text-gray-600">
              ← Back
            </button>
          )}

          <button
            disabled={!valid()}
            onClick={() =>
              step < 2 ? setStep(step + 1) : onComplete(form)
            }
            className={`ml-auto px-6 py-2 rounded-lg ${
              valid()
                ? "bg-emerald-600 text-white"
                : "bg-gray-200 text-gray-400"
            }`}
          >
            {step < 2 ? "Continue" : "Generate Payment Link"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= REUSABLE ================= */

const Input = ({ label, ...props }) => (
  <div>
    <label className="text-sm text-gray-600 mb-1 block">{label}</label>
    <input {...props} className="w-full border rounded-lg px-4 py-3 text-sm" />
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div>
    <label className="text-sm text-gray-600 mb-1 block">{label}</label>
    <select {...props} className="w-full border rounded-lg px-4 py-3 text-sm">
      {children}
    </select>
  </div>
);

const GatewayCard = ({ selected, onClick, img, title }) => (
  <button
    type="button"
    onClick={onClick}
    className={`border rounded-xl p-4 flex items-center gap-3 ${
      selected ? "border-emerald-500 ring-2 ring-emerald-200" : ""
    }`}
  >
    <img src={img} alt={title} className="h-8" />
    <span className="font-medium">{title}</span>
  </button>
);
